// SelfOnboardingConfigManagement.tsx
import React, { useRef, useState } from 'react';
import {Table, Button, Modal, Form, Input, Select, Switch, InputNumber, message, Tag, Spin, Divider} from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import { useCompany } from '@/hooks/company/use-company';
import {
    useCreateConfig, useDeleteConfig,
    useGetAllConfigs, useSetActiveConfig,
    useUpdateConfig
} from "@/modules/onboarding/hooks/useSelfOnboardingConfig";
import {useGetAllCustomerTypes} from "@/modules/hooks/useCustomerCategory";
import { UserSelect } from '@/modules/auth/components/UserSelect';
import {useGetPrimaryGroups} from "@/shared/api/client/nub-user-resource";
import {useGetGlAccounts} from "@/shared/api/client/coa-resource";
import {NubGroupDTO, SelfOnboardingConfigDTO, MapType, SelfRegistrationSessionDTO} from '@/shared/api/model';
import SelfRegistrationDeductionForm from '../../invitations/components/SelfRegistrationDeductionForm';
import DeductionFormConfigEditor from '../../invitations/components/DeductionFormConfigEditor';
// import { HAZINA_DEDUCTION_FORM_CONFIG, DeductionFormConfig } from '@/modules/loans/components/DeductionFormConfig'; // TODO: Component not implemented yet
import { useReactToPrint } from 'react-to-print';


const { Option } = Select;

const SelfOnboardingConfigManagement: React.FC = () => {
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingConfig, setEditingConfig] = useState<SelfOnboardingConfigDTO | null>(null);
    const [isPreviewVisible, setIsPreviewVisible] = useState(false);
    const [previewConfig, setPreviewConfig] = useState<SelfOnboardingConfigDTO | null>(null);
    // const [previewDeductionFormConfig, setPreviewDeductionFormConfig] = useState<DeductionFormConfig>(HAZINA_DEDUCTION_FORM_CONFIG); // TODO: Component not implemented yet
    const { dark } = useTheme();

    const { data: companyData } = useCompany();
    const previewPrintableRef = useRef<HTMLDivElement>(null);

    const handlePrintPreview = useReactToPrint({
        content: () => previewPrintableRef.current,
        documentTitle: `Self_Registration_Deduction_Form_Preview_${Date.now()}`,
        pageStyle: `
            @page {
                size: A4;
                margin: 10mm;
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                }
            }
        `,
    });

    const enableDirectBankDeposit = Form.useWatch('enableDirectBankDeposit', form);
    const depositDestinationType = Form.useWatch('depositDestinationType', form);

    const { data: configs, isLoading } = useGetAllConfigs();
    const { data: customerTypes } = useGetAllCustomerTypes();
    const { mutateAsync: createConfigAsync } = useCreateConfig();
    const { mutateAsync: updateConfigAsync } = useUpdateConfig();
    const { mutateAsync: deleteConfigAsync } = useDeleteConfig();
    const { mutateAsync: setActiveConfigAsync } = useSetActiveConfig();

    // Query primary groups
    const { data: groupsResponse, isLoading: isLoadingGroups } = useGetPrimaryGroups({
        query: { enabled: true }
    });

    // Query GL accounts for LIABILITIES
    const { data: glAccountsResponse, isLoading: isLoadingGlAccounts } = useGetGlAccounts(
        MapType.GL,
        // @ts-ignore
        { acType: 'EQUITY,LIABILITIES', q: '' },
        { query: { enabled: true } }
    );

    const showModal = (config?: SelfOnboardingConfigDTO) => {
        setEditingConfig(config || null);
        form.setFieldsValue({
            depositDestinationType: 'CUSTOMER_CREATED_ACCOUNT',
            requiresMemberSignedDeductionFormUpload: true,
            ...config,
        });
        setIsModalVisible(true);
    };

    const handleOk = () => {
        form.validateFields().then((values) => {
            const config: SelfOnboardingConfigDTO = {
                ...editingConfig,
                ...values,
                id: editingConfig ? editingConfig.id : undefined,
                enableMobileMoneyDeposit: !!values.enableMobileMoneyDeposit,
                enableBankPushDeposit: !!values.enableBankPushDeposit,
                enableDirectBankDeposit: !!values.enableDirectBankDeposit,
                requiresDeductionForm: !!values.requiresDeductionForm,
                requiresMemberSignedDeductionFormUpload: !!values.requiresDeductionForm && !!values.requiresMemberSignedDeductionFormUpload,
                depositDestinationType: values.depositDestinationType || 'CUSTOMER_CREATED_ACCOUNT',
                depositTargetGlNo: values.depositDestinationType === 'CONFIGURED_ACCOUNT' ? values.depositTargetGlNo : undefined,
                depositTargetAcNo: values.depositDestinationType === 'CONFIGURED_ACCOUNT' ? values.depositTargetAcNo : undefined,
                deductionFormConfig: values.deductionFormConfig,
            };

            if (editingConfig) {
                updateConfigAsync(config);
            } else {
                createConfigAsync(config);
            }

            setIsModalVisible(false);
            form.resetFields();
        });
    };

    const handleDelete = (record: SelfOnboardingConfigDTO) => {
        if(!record.id) return;
        deleteConfigAsync(record.id);
    };

    const handleSetActive = (record: SelfOnboardingConfigDTO) => {
        if(!record.id) return;
        setActiveConfigAsync(record.id);
    };

    const handlePreviewDeductionForm = (record: SelfOnboardingConfigDTO) => {
        // TODO: Deduction form preview not implemented - loans module missing
        setPreviewConfig(record);
        // let parsed: DeductionFormConfig = HAZINA_DEDUCTION_FORM_CONFIG;
        // if (record.deductionFormConfig) {
        //     try { parsed = JSON.parse(record.deductionFormConfig); } catch {}
        // }
        // setPreviewDeductionFormConfig(parsed);
        setIsPreviewVisible(true);
    };


    const handleUserChange = (userId: string) => {
        console.log('Selected user:', userId);
    };

    const columns = [
        {
            title: 'Default Customer Type',
            dataIndex: 'defaultCustomerType',
            key: 'defaultCustomerType',
        },
        {
            title: 'Initial Deposit GL',
            dataIndex: 'initialDepositGl',
            key: 'initialDepositGl',
        },
        {
            title: 'Deposit Target Mode',
            dataIndex: 'depositDestinationType',
            key: 'depositDestinationType',
            render: (value?: string) => value === 'CONFIGURED_ACCOUNT' ? 'Configured GL/Account' : 'Customer Created Account',
        },
        {
            title: 'Configured Deposit Target',
            key: 'configuredDepositTarget',
            render: (_: string, record: SelfOnboardingConfigDTO) => {
                if (record.depositDestinationType !== 'CONFIGURED_ACCOUNT') {
                    return '-';
                }
                return `${record.depositTargetGlNo || '-'} . ${record.depositTargetAcNo || '-'}`;
            },
        },
        {
            title: 'Min Deposit (TZS)',
            dataIndex: 'minDepositAmount',
            key: 'minDepositAmount',
            render: (amount: number) => `TZS ${amount.toLocaleString()}`,
        },
        {
            title: 'Max Deposit (TZS)',
            dataIndex: 'maxDepositAmount',
            key: 'maxDepositAmount',
            render: (amount: number) => `TZS ${amount.toLocaleString()}`,
        },
        {
            title: 'Min Monthly Contribution (TZS)',
            dataIndex: 'minimumMonthlyContribution',
            key: 'minimumMonthlyContribution',
            render: (amount?: number) => amount != null ? `TZS ${Number(amount).toLocaleString()}` : '-',
        },
        {
            title: 'Share Capital (TZS)',
            dataIndex: 'shareCapital',
            key: 'shareCapital',
            render: (amount?: number) => amount != null ? `TZS ${Number(amount).toLocaleString()}` : '-',
        },
        {
            title: 'Is Active',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => isActive ? 'Yes' : 'No',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text: string, record: SelfOnboardingConfigDTO) => (
                <>
                    <Button onClick={() => showModal(record)}>Edit</Button>
                    <Button onClick={() => handlePreviewDeductionForm(record)}>
                        Preview Deduction Form
                    </Button>
                    <Button onClick={() => handleDelete(record)} danger>Delete</Button>
                    {!record.isActive && (
                        <Button onClick={() => handleSetActive(record)}>Set Active</Button>
                    )}
                </>
            ),
        },
    ];

    if(isLoadingGroups || isLoadingGlAccounts) { 
        return <Spin size={"large"} tip="Loading configuration data..."/>
    }

    return (
        <div className={dark ? 'dark-mode' : ''}>
            <Button onClick={() => showModal()} type="primary" style={{ marginBottom: 16 }}>
                Add Configuration
            </Button>
            <Table
                dataSource={configs}
                columns={columns}
                loading={isLoading}
                rowKey="id"
            />
            <Modal
                title={editingConfig ? "Edit Configuration" : "Add Configuration"}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={() => setIsModalVisible(false)}
                width={800}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="defaultCustomerType"
                        label="Default Customer Type"
                        rules={[{ required: true, message: 'Please select the default customer type!' }]}
                    >
                        <Select placeholder="Select default customer type">
                            {customerTypes?.map(type => (
                                <Option key={type.code} value={type.code}>{type.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="initialDepositGl"
                        label="Initial Deposit GL"
                        rules={[{ required: true, message: 'Please select the initial deposit GL!' }]}
                    >
                        <Select 
                            placeholder="Select initial deposit GL (Liabilities)" 
                            showSearch
                            filterOption={(input, option) =>
                                (option?.children?.toString() as string)?.toLowerCase().includes(input.toLowerCase())
                            }
                            loading={isLoadingGlAccounts}
                        >
                            {glAccountsResponse?.map((account) => (
                                <Option key={account.accountId} value={account.accountId}>
                                    {account.glName} ({account.accountId})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider orientation="left">Approved Deposit Posting Target</Divider>
                    <Form.Item
                        name="depositDestinationType"
                        label="Deposit Destination"
                        initialValue="CUSTOMER_CREATED_ACCOUNT"
                        rules={[{ required: true, message: 'Please select the deposit destination mode!' }]}
                    >
                        <Select placeholder="Select deposit destination">
                            <Option value="CUSTOMER_CREATED_ACCOUNT">Post to customer created account</Option>
                            <Option value="CONFIGURED_ACCOUNT">Post to configured GL/account</Option>
                        </Select>
                    </Form.Item>

                    {depositDestinationType === 'CONFIGURED_ACCOUNT' && (
                        <>
                            <Form.Item
                                name="depositTargetGlNo"
                                label="Configured Deposit Target GL"
                                rules={[{ required: true, message: 'Please enter the configured deposit target GL!' }]}
                            >
                                <Input placeholder="Enter configured target GL number" />
                            </Form.Item>

                            <Form.Item
                                name="depositTargetAcNo"
                                label="Configured Deposit Target Account"
                                rules={[{ required: true, message: 'Please enter the configured deposit target account!' }]}
                            >
                                <Input placeholder="Enter configured target account number" />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item
                        name="minDepositAmount"
                        label="Minimum Deposit Amount (TZS)"
                        rules={[{ required: true, message: 'Please enter the minimum deposit amount!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={value => `TZS ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            // @ts-ignore
                            parser={value => value!.replace(/TZS\s?|(,*)/g, '')}
                            min={0}
                        />
                    </Form.Item>

                    <Form.Item
                        name="maxDepositAmount"
                        label="Maximum Deposit Amount (TZS)"
                        rules={[{ required: true, message: 'Please enter the maximum deposit amount!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={value => `TZS ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            // @ts-ignore
                            parser={value => value!.replace(/TZS\s?|(,*)/g, '')}
                            min={0}
                        />
                    </Form.Item>

                    <Form.Item
                        name="minimumMonthlyContribution"
                        label="Minimum Monthly Contribution (TZS)"
                        rules={[{ required: true, message: 'Please enter the minimum monthly contribution!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={value => `TZS ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            // @ts-ignore
                            parser={value => value!.replace(/TZS\s?|(,*)/g, '')}
                            min={0}
                        />
                    </Form.Item>

                    <Form.Item
                        name="shareCapital"
                        label="Share Capital (TZS)"
                        rules={[{ required: true, message: 'Please enter the share capital!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={value => `TZS ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            // @ts-ignore
                            parser={value => value!.replace(/TZS\s?|(,*)/g, '')}
                            min={0}
                        />
                    </Form.Item>

                    <Form.Item name="primaryGroupCode" label="Primary Group" rules={[{ required: true, message: 'Primary Group is required' }]}>
                        <Select placeholder="Select Group" loading={isLoadingGroups}>
                            {groupsResponse?.map((groupDTO: NubGroupDTO) => (
                                <Option key={groupDTO.code} value={groupDTO.code}>
                                    {groupDTO.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="userCreator"
                        label="User Creator"
                        rules={[{ required: true, message: 'Please select a user' }]}
                    >
                        <UserSelect
                            style={{ width: '100%' }}
                            placeholder="Search by name or user ID"
                            onChange={handleUserChange}
                        />
                    </Form.Item>
                    <Form.Item name="requiresOfficerApproval" valuePropName="checked" label="Requires Officer Approval">
                        <Switch />
                    </Form.Item>

                    <Form.Item name="requiresDeductionForm" valuePropName="checked" label="Requires Deduction Form"
                        tooltip="When enabled, officers must prepare/upload the deduction form after deposit approval as part of finalizing registration">
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="requiresMemberSignedDeductionFormUpload"
                        valuePropName="checked"
                        label="Require Member To Upload Signed Deduction Form"
                        tooltip="When enabled, the member must upload the signed deduction form back for officer review before registration is finalized. Disable this if the member will keep the signed copy after the officer uploads the official deduction form."
                    >
                        <Switch />
                    </Form.Item>

                    <Divider orientation="left">Deduction Form Appearance</Divider>
                    <Form.Item
                        name="deductionFormConfig"
                        tooltip="Configure logo, stamp and watermark for the deduction form. Leave empty to use system defaults."
                    >
                        <DeductionFormConfigEditor
                            onPreview={(cfg) => {
                                setPreviewConfig((form.getFieldsValue(true) as SelfOnboardingConfigDTO) || ({} as SelfOnboardingConfigDTO));
                                // setPreviewDeductionFormConfig(cfg); // TODO: Component not implemented yet
                                setIsPreviewVisible(true);
                            }}
                        />
                    </Form.Item>

                    <Form.Item name="enableMobileMoneyDeposit" valuePropName="checked" label="Enable Mobile Money Deposit">
                        <Switch />
                    </Form.Item>

                    <Form.Item name="enableBankPushDeposit" valuePropName="checked" label="Enable Bank Push Deposit">
                        <Switch />
                    </Form.Item>

                    <Form.Item name="enableDirectBankDeposit" valuePropName="checked" label="Enable Direct Bank Deposit">
                        <Switch />
                    </Form.Item>

                    {enableDirectBankDeposit && (
                        <>
                            <Form.Item
                                name="directDepositBankName"
                                label="Direct Deposit Bank Name"
                                rules={[{ required: true, message: 'Bank name is required' }]}
                            >
                                <Input placeholder="Enter bank name" />
                            </Form.Item>
                            <Form.Item
                                name="directDepositAccountName"
                                label="Direct Deposit Account Name"
                                rules={[{ required: true, message: 'Account name is required' }]}
                            >
                                <Input placeholder="Enter account name" />
                            </Form.Item>
                            <Form.Item
                                name="directDepositAccountNumber"
                                label="Direct Deposit Account Number"
                                rules={[{ required: true, message: 'Account number is required' }]}
                            >
                                <Input placeholder="Enter account number" />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>

            <Modal
                title="Deduction Form Preview"
                open={isPreviewVisible}
                onCancel={() => {
                    setIsPreviewVisible(false);
                    setPreviewConfig(null);
                }}
                width={950}
                footer={[
                    <Button
                        key="print"
                        type="primary"
                        onClick={() => handlePrintPreview()}
                        disabled={!previewConfig}
                    >
                        Print
                    </Button>,
                    <Button
                        key="close"
                        onClick={() => {
                            setIsPreviewVisible(false);
                            setPreviewConfig(null);
                        }}
                    >
                        Close
                    </Button>
                ]}
            >
                {previewConfig && (
                    <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: 12, background: '#fff' }}>
                        <div ref={previewPrintableRef}>
                            <SelfRegistrationDeductionForm
                                session={{
                                    sessionToken: 'PREVIEW',
                                    fullName: 'JOHN DOE',
                                    employeeId: '11111111',
                                    voteNumber: 'S11',
                                    monthlyDeductionAmount: Number(previewConfig.minimumMonthlyContribution ?? 50000),
                                    employerName: 'TANZANIA WILDLIFE AUTHORITY (TAWA)',
                                    officerApprovedBy: 'OFFICER NAME',
                                } as SelfRegistrationSessionDTO}
                                company={previewConfig.deductionFormConfig ? JSON.parse(previewConfig.deductionFormConfig)?.company : undefined}
                                deductionFormConfig={previewConfig.deductionFormConfig ? JSON.parse(previewConfig.deductionFormConfig) : undefined}
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default SelfOnboardingConfigManagement;